import { createAsyncThunk, createSlice, unwrapResult } from "@reduxjs/toolkit";
import SubscriptionState from "interfaces/state/SubscriptionState";
import {
  getSubscriptionBySubscriptionIdAsync,
  changeSubscription,
  registerToService,
  subscribeToDefaultProductAndDefaultFreeSubscriptionAsync,
  getSubscriptionUpdatePreviewAsync,
  cancelScheduledSubscriptionChange,
} from "apis/SubscriptionApi";
import ChangeSubscriptionRequest from "interfaces/request/ChangeSubscriptionRequest";
import SubscriptionResponse from "interfaces/response/SubscriptionResponse";
import { RootState } from "store/store";

const initialState: SubscriptionState = {
  loading: false,
  selectedProductSubscriptionId: 0,
  selectedProductSubscription: null,
  subscriptionChangePreview: null,
};

export const getSubscription = createAsyncThunk(
  "subscription/getSubscription",
  async (subscriptionId: number) => {
    return await getSubscriptionBySubscriptionIdAsync(subscriptionId);
  }
);

export const registerSubscriptionWithSelectedProduct = createAsyncThunk<
  SubscriptionResponse,
  ChangeSubscriptionRequest,
  { state: RootState }
>(
  "subscription/registerSubscriptionWithSelectedProduct",
  async (serviceRegisterRequestData: ChangeSubscriptionRequest, thunkApi) => {
    const store = thunkApi.getState();
    const selectedProductId = store.productData.selectedProductId;
    return thunkApi
      .dispatch(
        registerUserWithSubscription({
          productId: selectedProductId,
          subscriptionRequest: serviceRegisterRequestData,
        })
      )
      .then(unwrapResult);
  }
);

export const registerSubscriptionWithDefaultProductAndDefaultLicense =
  createAsyncThunk(
    "subscription/registerSubscriptionWithDefaultProductAndDefaultLicense",
    async (_, thunkApi) => {
      return thunkApi.dispatch(
        subscribeToDefaultProductAndDefaultFreeSubscriptionAsync
      );
    }
  );

export const registerUserWithSubscription = createAsyncThunk(
  "subscription/registerService",
  async (data: {
    productId: number;
    subscriptionRequest: ChangeSubscriptionRequest;
  }) => {
    return await registerToService(data.productId, data.subscriptionRequest);
  }
);

export const changeExistingSubscription = createAsyncThunk(
  "product/changeExistingSubscription",
  async (data: {
    subscriptionId: number;
    subscriptionChangeRequest: ChangeSubscriptionRequest;
  }) => {
    return await changeSubscription(
      data.subscriptionId,
      data.subscriptionChangeRequest
    );
  }
);

export const previewChangeSubscription = createAsyncThunk(
  "product/previewChangeSubscription",
  async (data: {
    subscriptionId: number;
    subscriptionChangeRequest: ChangeSubscriptionRequest;
  }) => {
    return await getSubscriptionUpdatePreviewAsync(
      data.subscriptionId,
      data.subscriptionChangeRequest
    );
  }
);

export const cancelScheduledSubscriptionChangeAsync = createAsyncThunk(
  "subscription/cancelScheduledSubscriptionChangeAsync",
  async (subscriptionId: number) => {
    return await cancelScheduledSubscriptionChange(subscriptionId);
  }
);

export const subscriptionSlice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setSelectedProductSubscriptionId: (state, action) => {
      state.selectedProductSubscriptionId = action.payload;
    },
    updateCurrentPeriodEndTimeWhenBillingIntervalIsChanging: (
      state,
      action
    ) => {
      state.selectedProductSubscription!.currentPeriodFinish = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSubscription.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getSubscription.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedProductSubscription = action.payload;
      state.selectedProductSubscriptionId = action.payload.id;
    });
    builder.addCase(getSubscription.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(changeExistingSubscription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changeExistingSubscription.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(changeExistingSubscription.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(registerUserWithSubscription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(registerUserWithSubscription.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(registerUserWithSubscription.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(previewChangeSubscription.pending, (state) => {
      state.subscriptionChangePreview = null;
      state.loading = true;
    });
    builder.addCase(previewChangeSubscription.fulfilled, (state, action) => {
      state.subscriptionChangePreview = action.payload;
      state.loading = false;
    });
    builder.addCase(previewChangeSubscription.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(cancelScheduledSubscriptionChangeAsync.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      cancelScheduledSubscriptionChangeAsync.fulfilled,
      (state, action) => {
        if (
          state.selectedProductSubscription?.subscriptionScheduledOperations
        ) {
          state.selectedProductSubscription.subscriptionScheduledOperations =
            [];
        }
        state.loading = false;
      }
    );
    builder.addCase(
      cancelScheduledSubscriptionChangeAsync.rejected,
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const {
  setSelectedProductSubscriptionId,
  updateCurrentPeriodEndTimeWhenBillingIntervalIsChanging,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
